import React, { useEffect, useRef } from "react";
import { Heading, Text, VStack, Button } from "@chakra-ui/react";
import ContentContainer from "components/Container";
import { useOverlaySpinner } from "components/OverlaySpinner";
// eslint-disable-next-line import/no-unresolved
import SadComputer from "assets/empty-states/sad-computer.svg?react"; // no-unresolved gets triggered by '?react' suffix
import { usePostMessage, useSessionCtx, FLOW_STATE, noop, useAnalytics } from "utils";
import { useFlowConfig } from "flows/hooks";

/**
 * EmptyState
 * @description
 * A component that handles the displaying of empty states. Useful for full page error handling
 * or to indicate that no data can be found. When provided with an 'error' prop, it renders
 * with error based colors. If not, it renders more neutral.
 * @param {Object} props
 * @param {unknown} [props.error] An Error object which, when provided, is massaged and rendered
 * @param {string} [props.title] Title for the empty state
 * @param {string} [props.description] Description for the empty state
 * @param {OnReady} [props.onReady=noop] An optional onReady callback for use in some full page error cases where onReady needs to fire
 * @param {boolean} [props.hideButtons=false] To hide 'Close' button, wich can be done through this or if the flowConfig contains hideExitButtons: true
 * @returns
 */
export default function EmptyState({
  error,
  title: _title,
  description: _description,
  onReady = noop,
  hideButtons = false,
}) {
  useEffect(onReady, [onReady]);
  const { sessionCtx } = useSessionCtx();
  const { hideExitButtons } = useFlowConfig();
  const { postOnComplete } = usePostMessage((sessionCtx && sessionCtx.flow) || "", sessionCtx?.intCtx);
  const { hideOverlaySpinner } = useOverlaySpinner();
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);

  useEffect(() => {
    hideOverlaySpinner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let title = _title || "Not found";
  let description =
    _description || "Sorry, but whatever you were looking for doesn't seem to exist.";

  const handleCloseButton = () => {
    postOnComplete({
      state: FLOW_STATE.ERROR,
      trigger: "close-button",
    });
  };

  // If we get an error to display, we try to figure out what to display to our users
  // based on the content of the error
  if (error) {
    title = _title || "Oops!";
    description =
      "We seem to have hit a snag that we're unable to untangle at the moment. Sorry for the inconvenience.";

    if (error instanceof Error && error.message) {
      description = error.message;
    }
  }

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("EmptyState", "EmptyState_Viewed", {
        title,
        description,
      });
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired, title, description]);

  return (
    <ContentContainer centerContent maxW="sm" data-testid="empty-state-container">
      <VStack spacing={0}>
        <SadComputer />
        <VStack spacing={2}>
          <Heading size="xl">{title}</Heading>
          <Text color="secondaryText" textAlign="left">
            {description}
          </Text>
        </VStack>
      </VStack>
      {!hideExitButtons && !hideButtons ? (
        <Button onClick={handleCloseButton} variant="outline" mt={4}>
          Close
        </Button>
      ) : null}
    </ContentContainer>
  );
}
